import { Menu, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { IconButton } from "../atoms/IconButton";
import { Link } from "../atoms/Link";
import { cn } from "../lib/cn";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogTitle,
} from "../primitives/Dialog";

export interface NavItem {
	label: string;
	href: string;
	isActive?: boolean;
}

/** Helpers handed to a `search` render function so it can react to header state. */
export interface AppHeaderSearchHelpers {
	/** Closes the mobile navigation menu (no-op for the desktop instance). */
	closeMobileMenu: () => void;
}

const noop = (): void => undefined;

export interface AppHeaderProps {
	logoSlot?: ReactNode;
	navItems?: NavItem[];
	/**
	 * Persistent search control, rendered on desktop and inside the mobile menu.
	 * Pass a `ReactNode`, or a render function that receives helpers (e.g. to
	 * close the mobile menu after navigating).
	 */
	search?: ReactNode | ((helpers: AppHeaderSearchHelpers) => ReactNode);
	actions?: ReactNode;
	className?: string;
}

function AppHeader({
	logoSlot,
	navItems = [],
	search,
	actions,
	className,
}: AppHeaderProps): ReactNode {
	const [mobileOpen, setMobileOpen] = useState(false);
	const showMobileMenu = navItems.length > 0 || Boolean(search);

	const renderSearch = (closeMobileMenu: () => void): ReactNode =>
		typeof search === "function" ? search({ closeMobileMenu }) : search;

	return (
		<header
			className={cn(
				"flex h-14 items-center border-b border-border bg-background px-4 gap-6",
				className,
			)}
		>
			{logoSlot && <div className="shrink-0">{logoSlot}</div>}

			{/* Desktop nav — visible at md and above */}
			{navItems.length > 0 && (
				<nav
					aria-label="Main navigation"
					className="hidden md:flex items-center gap-1"
				>
					{navItems.map((item) => (
						<Link
							key={item.href}
							href={item.href}
							className={cn(
								"rounded-md px-3 py-1.5 text-sm font-medium no-underline transition-colors hover:bg-accent hover:text-accentForeground",
								item.isActive
									? "bg-accent text-accentForeground"
									: "text-mutedForeground",
							)}
						>
							{item.label}
						</Link>
					))}
				</nav>
			)}

			{/* Spacer so search/actions always align to right */}
			<div className="flex-1" />

			{/* Desktop search — visible at md and above; mobile uses the menu */}
			{search && (
				<div className="hidden md:block w-64">{renderSearch(noop)}</div>
			)}

			{actions && <div className="flex items-center gap-2">{actions}</div>}

			{/* Mobile hamburger — visible below md */}
			{showMobileMenu && (
				<div className="md:hidden">
					<IconButton
						aria-label="Open navigation menu"
						variant="ghost"
						size="sm"
						onClick={() => setMobileOpen(true)}
					>
						<Menu className="h-5 w-5" aria-hidden="true" />
					</IconButton>

					<Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
						<DialogContent
							className="left-0 top-0 translate-x-0 translate-y-0 max-w-xs w-4/5 h-full rounded-none border-r border-border p-0"
							showClose={false}
							aria-describedby={undefined}
						>
							<div className="flex items-center justify-between px-4 h-14 border-b border-border">
								<DialogTitle className="text-sm font-semibold">
									Navigation
								</DialogTitle>
								<DialogClose asChild={true}>
									<IconButton
										aria-label="Close navigation menu"
										variant="ghost"
										size="sm"
									>
										<X className="h-5 w-5" aria-hidden="true" />
									</IconButton>
								</DialogClose>
							</div>
							{search && (
								<div className="p-4 pb-0">
									{renderSearch(() => setMobileOpen(false))}
								</div>
							)}
							{navItems.length > 0 && (
								<nav
									aria-label="Mobile navigation"
									className="flex flex-col p-4 gap-1"
								>
									{navItems.map((item) => (
										<Link
											key={item.href}
											href={item.href}
											onClick={() => setMobileOpen(false)}
											className={cn(
												"rounded-md px-3 py-2 text-sm font-medium no-underline transition-colors hover:bg-accent hover:text-accentForeground",
												item.isActive
													? "bg-accent text-accentForeground"
													: "text-mutedForeground",
											)}
										>
											{item.label}
										</Link>
									))}
								</nav>
							)}
						</DialogContent>
					</Dialog>
				</div>
			)}
		</header>
	);
}
AppHeader.displayName = "AppHeader";

export default AppHeader;
export { AppHeader };
